import { useState, useEffect } from 'react';
import {
	Drawer,
	Box,
	Typography,
	IconButton,
	TextField,
	Button,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	CircularProgress,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { authedApiFetch } from '../../../utils/Api';

/**
 * DealDrawer component
 * A responsive drawer for creating new deals or editing existing deals
 * Appears from right side on desktop and bottom on mobile
 */
const DealDrawer = ({
	open,
	onClose,
	onSave,
	tenantId,
	editMode = false,
	dealData = null,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [loading, setLoading] = useState(false);
	const [stageOptions, setStageOptions] = useState([]);
	const [currencyOptions, setCurrencyOptions] = useState([]);
	const [_currentUser, setCurrentUser] = useState(null);

	const [formData, setFormData] = useState({
		name: '',
		company_info: { name: '' },
		fund_info: { name: '' },
		deal_size: 0,
		currency: null, // Will store config list item ID
		stage: null, // Will store config list item ID
	});

	// Initialize form data when drawer is opened
	useEffect(() => {
		if (open) {
			if (editMode && dealData) {
				// Populate with existing deal data when editing
				setFormData({
					name: dealData.name || '',
					company_info: {
						name: dealData.company_info?.name || '',
					},
					fund_info: {
						name: dealData.fund_info?.name || '',
					},
					deal_size: dealData.deal_size || 0,
					currency: dealData.currency || null,
					stage: dealData.stage || null,
				});
			} else {
				// Reset form for new creation
				setFormData({
					name: '',
					company_info: { name: '' },
					fund_info: { name: '' },
					deal_size: 0,
					currency: null,
					stage: null,
				});
			}
			fetchConfigData();
		}
	}, [open, tenantId, editMode, dealData]);

	const fetchConfigData = async () => {
		try {
			// Fetch stage options (using deal_stages config list)
			const stageResponse = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/config_lists?list_type=deal_stages`,
				method: 'GET',
			});

			if (
				stageResponse &&
				Array.isArray(stageResponse) &&
				stageResponse.length > 0 &&
				stageResponse[0]?.config_list_items
			) {
				const stageItems = stageResponse[0].config_list_items;
				setStageOptions(stageItems);

				// Set default stage if available
				if (stageItems.length > 0) {
					setFormData((prev) => ({
						...prev,
						stage: stageItems[0].id, // Use ID, not value
					}));
				}
			}

			// Fetch currency options
			const currencyResponse = await authedApiFetch({
				endpoint: `/tenants/${tenantId}/config_lists?list_type=currencies`,
				method: 'GET',
			});

			if (
				currencyResponse &&
				Array.isArray(currencyResponse) &&
				currencyResponse.length > 0 &&
				currencyResponse[0]?.config_list_items
			) {
				const currencyItems = currencyResponse[0].config_list_items;
				setCurrencyOptions(currencyItems);

				// Set default currency if available
				if (currencyItems.length > 0) {
					setFormData((prev) => ({
						...prev,
						currency: editMode
							? currencyItems.find((item) => item.id === dealData.currency)?.id
							: currencyItems[0].id, // Use ID, not value
					}));
				}
			}

			// Fetch current user (if needed)
			try {
				const userResponse = await authedApiFetch({
					endpoint: '/users/current',
					method: 'GET',
				});

				if (userResponse) {
					setCurrentUser(userResponse);
					// We're not setting owner_id at all - the backend should handle this automatically
					// using the current authenticated user
				}
			} catch (userError) {
				console.error('Error fetching current user:', userError);
				// Continue even if user fetch fails
			}
		} catch (error) {
			console.error('Error fetching config data:', error);
		}
	};

	const handleChange = (e) => {
		const { name, value } = e.target;

		// Handle nested fields
		if (name.includes('.')) {
			const [parent, child] = name.split('.');
			setFormData((prev) => ({
				...prev,
				[parent]: {
					...prev[parent],
					[child]: value,
				},
			}));
		} else {
			setFormData((prev) => ({
				...prev,
				[name]: value,
			}));
		}
	};

	const handleSubmit = async () => {
		setLoading(true);
		try {
			// Create payload
			const dealPayload = {
				name: formData.name || 'New Deal',
				stage: formData.stage,
				deal_size: Number(formData.deal_size) || 0,
				currency: formData.currency,
				company_info: formData.company_info,
				fund_info: formData.fund_info,
				active: true,
			};

			let response;

			if (editMode && dealData) {
				// Update existing deal
				response = await authedApiFetch({
					endpoint: `/tenants/${tenantId}/deals/${dealData.id}`,
					method: 'PATCH',
					payload: { deal: dealPayload },
				});
			} else {
				// Create new deal
				response = await authedApiFetch({
					endpoint: `/tenants/${tenantId}/deals`,
					method: 'POST',
					payload: { deal: dealPayload },
				});
			}

			setLoading(false);
			onSave(response);
			onClose();
		} catch (error) {
			console.error(`Error ${editMode ? 'updating' : 'creating'} deal:`, error);
			setLoading(false);
		}
	};

	return (
		<Drawer
			anchor={isMobile ? 'bottom' : 'right'}
			open={open}
			onClose={onClose}
			PaperProps={{
				sx: {
					width: isMobile ? '100%' : '500px',
					maxHeight: isMobile ? '90vh' : '100%',
					borderRadius: isMobile ? '16px 16px 0 0' : '0',
					padding: 0,
					zIndex: 1300,
				},
			}}
		>
			<Box
				sx={{
					p: 0,
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
				}}
			>
				{/* Drawer Header */}
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						borderBottom: `1px solid ${theme.palette.divider}`,
						p: 2,
					}}
				>
					<Typography variant='h6'>
						{editMode ? 'Edit Deal' : 'New Deal'}
					</Typography>
					<IconButton onClick={onClose} edge='end' aria-label='close'>
						<CloseIcon />
					</IconButton>
				</Box>

				{/* Form Container */}
				<Box
					sx={{
						p: 2,
						flexGrow: 1,
						overflowY: 'auto',
					}}
				>
					<TextField
						fullWidth
						margin='normal'
						label='Deal Name'
						name='name'
						value={formData.name}
						onChange={handleChange}
						required
					/>

					<TextField
						fullWidth
						margin='normal'
						label='Company Name'
						name='company_info.name'
						value={formData.company_info.name}
						onChange={handleChange}
					/>

					<TextField
						fullWidth
						margin='normal'
						label='Fund Name'
						name='fund_info.name'
						value={formData.fund_info.name}
						onChange={handleChange}
					/>

					<TextField
						fullWidth
						margin='normal'
						label='Deal Size'
						name='deal_size'
						type='number'
						value={formData.deal_size}
						onChange={handleChange}
					/>

					<FormControl fullWidth margin='normal'>
						<InputLabel>Currency</InputLabel>
						<Select
							name='currency'
							value={formData.currency || ''}
							onChange={handleChange}
							label='Currency'
						>
							{currencyOptions.map((currency) => (
								<MenuItem key={currency.id} value={currency.id}>
									{currency.display_name}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<FormControl fullWidth margin='normal'>
						<InputLabel>Deal Stage</InputLabel>
						<Select
							name='stage'
							value={formData.stage || ''}
							onChange={handleChange}
							label='Deal Stage'
						>
							{stageOptions.map((stage) => (
								<MenuItem key={stage.id} value={stage.id}>
									{stage.display_name}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
						<Button
							variant='outlined'
							onClick={onClose}
							sx={{ mr: 1 }}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button
							variant='contained'
							onClick={handleSubmit}
							disabled={loading}
						>
							{loading ? (
								<CircularProgress size={24} />
							) : editMode ? (
								'Save Changes'
							) : (
								'Create Deal'
							)}
						</Button>
					</Box>
				</Box>
			</Box>
		</Drawer>
	);
};

export default DealDrawer;
